import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import SearchResults from "@/components/SearchResults";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search } from "lucide-react";

export default function GlobalSearch({ category, className = "", open: controlledOpen, onOpenChange, hideTrigger = false }) {
  const navigate = useNavigate();
  const [uOpen, setUOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : uOpen;
  const setOpen = onOpenChange || setUOpen;
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ projects: [], tasks: [] });
  const [loading, setLoading] = useState(false);
  const timer = useRef();

  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    const query = q.trim();
    if (query.length < 2) { setRes({ projects: [], tasks: [] }); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: query, ...(category ? { category } : {}) } });
        setRes(data);
      } catch (e) { if (process.env.NODE_ENV === "development") console.warn("Search request failed:", e); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q, open, category]);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const query = q.trim();
  const empty = !loading && query.length >= 2 && res.projects.length === 0 && res.tasks.length === 0;

  return (
    <>
      {!hideTrigger && (
        <button
          onClick={() => setOpen(true)}
          data-testid="global-search-button"
          className={`inline-flex items-center gap-2 h-10 px-3 rounded-sm border border-zinc-200 bg-white text-sm text-zinc-500 hover:border-zinc-400 transition-colors ${className}`}
        >
          <Search className="h-4 w-4" />
          <span className="hidden sm:inline">Search…</span>
        </button>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
        <DialogContent className="rounded-sm max-w-lg w-[calc(100vw-2rem)] sm:w-full p-0 overflow-hidden gap-0" data-testid="global-search-dialog">
          <DialogHeader className="sr-only">
            <DialogTitle>Search projects and tasks</DialogTitle>
            <DialogDescription>Type at least two characters to find projects and tasks across the studio.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 border-b border-zinc-200 px-3">
            <Search className="h-4 w-4 text-zinc-400 shrink-0" />
            <input
              autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={category ? "Search this folder…" : "Search projects, clients & tasks…"}
              className="flex-1 h-12 outline-none text-sm bg-transparent"
              data-testid="global-search-input"
            />
          </div>
          <SearchResults q={q} query={query} loading={loading} res={res} empty={empty} onGo={go} />
        </DialogContent>
      </Dialog>
    </>
  );
}
